import {
  type AppointmentWithDisplay,
  IMPORTANT_MARK,
  type LocalMonth,
  type SchoolHoliday,
  holidayOn,
} from '@ergo/shared';
import { api } from '../api';
import { OutcomeButtons } from './OutcomeButtons';
import { useResource } from '../hooks';
import { useSchoolHolidays } from '../holidays';
import {
  addDays,
  addMonths,
  daysBetween,
  endsAt,
  formatDuration,
  formatMonthLong,
  now,
  timeOf,
  today,
} from '../time';

/**
 * Le mois qui se vit, vu du Rapport (docs/redesign-mois-en-cours.md) : pas une
 * clôture inachevée, un mois en cours. Ni anneau ni étapes — ce qui est déjà fait,
 * où on en est dans le mois, et la seule chose qui mérite un mot : une séance
 * passée encore sans durée. Les mois futurs passent par la même carte, réduite à
 * leurs jours prévus.
 */
export function MoisEnCours({ month, onChanged }: { month: LocalMonth; onChanged: () => void }) {
  const first = `${month}-01`;
  const last = addDays(`${addMonths(month, 1)}-01`, -1);
  const seances = useResource(() => api.appointments.list({ from: first, to: last }), [month]);
  const holidays = useSchoolHolidays(first, last);

  const items = seances.data;
  if (!items) return null;

  const enCours = month === today().slice(0, 7);
  const mois = formatMonthLong(month);
  const nowAt = now();

  const faites = items.filter((item) => item.status === 'done');
  const minutes = faites.reduce((total, item) => total + (item.actualDurationMin ?? 0), 0);
  const familles = new Set(faites.map((item) => item.patientId)).size;
  // Les séances passées encore « prévues » : la seule chose que le mois demande.
  const enAttente = items
    .filter(
      (item) => item.status === 'planned' && endsAt(item.startsAt, item.plannedDurationMin) <= nowAt,
    )
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt));

  return (
    <>
      <section className="encours">
        <header>
          <p className="encours__overline">
            {mois} {enCours ? 'en cours' : 'à venir'}
          </p>
          <h2 className="encours__title">{titreDe(faites.length)}</h2>
          {faites.length > 0 && (
            <p className="encours__sub">
              {formatDuration(minutes)} · {familles} famille{familles > 1 ? 's' : ''}
              {enAttente.length === 0 && ' · toutes pointées ✓'}
            </p>
          )}
        </header>

        <BarreDesJours
          month={month}
          last={last}
          items={items}
          holidays={holidays}
          enCours={enCours}
        />

        <p className="calme">Rien à boucler pour l'instant.</p>

        {enAttente.length > 0 && (
          <div className="nudge">
            <p className="nudge__ligne">
              <span className="nudge__dot" aria-hidden="true" />
              {nudgeDe(enAttente)}
            </p>
            <div className="nudge__seance">
              <p className={`nudge__quoi${enAttente[0]!.important ? ' is-important' : ''}`}>
                {enAttente[0]!.important && `${IMPORTANT_MARK} `}
                {/* Le patient est déjà dit au-dessus : l'intitulé prend la place du mot
                    « Séance », il ne s'y ajoute pas. */}
                {enAttente[0]!.title ?? 'Séance'} du {enAttente[0]!.startsAt.slice(8, 10)}/
                {enAttente[0]!.startsAt.slice(5, 7)}{' '}
                · {timeOf(enAttente[0]!.startsAt)} · {enAttente[0]!.locationLabel}
              </p>
              <OutcomeButtons appointment={enAttente[0]!} onLogged={onChanged} />
            </div>
          </div>
        )}
      </section>

      <DejaFait faites={faites} />
    </>
  );
}

function titreDe(count: number): string {
  if (count === 0) return '0 séance pour l’instant';
  if (count === 1) return '1 séance déjà faite';
  return `${count} séances déjà faites`;
}

function nudgeDe(enAttente: AppointmentWithDisplay[]): string {
  if (enAttente.length > 1)
    return `${enAttente.length} séances attendent leur durée, quand tu veux.`;
  const date = enAttente[0]!.startsAt.slice(0, 10);
  if (date === addDays(today(), -1)) return 'La séance d’hier attend sa durée, quand tu veux.';
  const day = Number(date.slice(8, 10));
  return `La séance du ${day === 1 ? '1er' : day} attend sa durée, quand tu veux.`;
}

/**
 * Un segment par jour : le mois se lit comme une frise, pas comme un tableau. Un
 * jour passé n'est bleu que s'il porte une séance au sort réglé — un jour au
 * pointage en retard reste éteint, c'est le nudge qui en parle.
 */
function BarreDesJours({
  month,
  last,
  items,
  holidays,
  enCours,
}: {
  month: LocalMonth;
  last: string;
  items: AppointmentWithDisplay[];
  holidays: SchoolHoliday[];
  enCours: boolean;
}) {
  const jours = Number(last.slice(8, 10));
  const aujourdhui = today();
  const parJour = new Map<string, AppointmentWithDisplay[]>();
  for (const item of items) {
    const day = item.startsAt.slice(0, 10);
    parJour.set(day, [...(parJour.get(day) ?? []), item]);
  }

  const aVenir = [...parJour.entries()].filter(
    ([day, list]) => day > aujourdhui && list.some((item) => item.status === 'planned'),
  ).length;

  return (
    <div>
      <div className="jours" aria-hidden="true">
        {Array.from({ length: jours }, (_, index) => {
          const day = `${month}-${String(index + 1).padStart(2, '0')}`;
          const list = parJour.get(day) ?? [];
          const passe = day <= aujourdhui;
          const kind = passe
            ? list.some((item) => item.status !== 'planned')
              ? 'fait'
              : 'vide'
            : list.length > 0
              ? 'prevu'
              : 'futur';
          // Les vacances scolaires ne changent pas la couleur du jour — elles la
          // voilent. La bulle, elle, dit lesquelles : c'est là qu'on le cherche.
          const vacances = holidayOn(day, holidays);
          const classes = `jours__seg jours__seg--${kind}${vacances ? ' jours__seg--vacances' : ''}${day === aujourdhui ? ' jours__seg--auj' : ''}`;
          const titre = `${index + 1} ${formatMonthLong(month)}${vacances ? ` · ${vacances.name}` : ''}`;
          return <span key={day} className={classes} title={titre} />;
        })}
      </div>
      <p className="jours__legende">
        <span>{enCours ? `Jour ${daysBetween(`${month}-01`, aujourdhui) + 1} sur ${jours}` : ''}</span>
        <span>
          {aVenir > 0 &&
            (aVenir === 1 ? '1 jour de séances à venir' : `${aVenir} jours de séances à venir`)}
        </span>
      </p>
    </div>
  );
}

/** Ce qui est déjà fait, patient par patient — un compte rond, rien à déplier. */
function DejaFait({ faites }: { faites: AppointmentWithDisplay[] }) {
  const parPatient = new Map<number, { name: string; count: number; minutes: number }>();
  for (const item of faites) {
    const entry = parPatient.get(item.patientId) ?? { name: item.patientName, count: 0, minutes: 0 };
    entry.count += 1;
    entry.minutes += item.actualDurationMin ?? 0;
    parPatient.set(item.patientId, entry);
  }
  const lignes = [...parPatient.values()].sort(
    (a, b) => b.count - a.count || a.name.localeCompare(b.name, 'fr'),
  );
  if (lignes.length === 0) return null;

  return (
    <section>
      <h2 className="ensuite__title">Déjà fait ce mois-ci</h2>
      <ul className="dejafait">
        {lignes.map((ligne) => (
          <li className="dejafait__ligne" key={ligne.name}>
            <span className="dejafait__rond">{ligne.count}</span>
            <span className="dejafait__corps">
              <span className="dejafait__nom">{ligne.name}</span>
              <span className="dejafait__sous">
                {ligne.count} séance{ligne.count > 1 ? 's' : ''} · {formatDuration(ligne.minutes)}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
